<script lang="ts">
  import { groupBy } from 'lodash-es'
  import { icon } from '#app/lib/icons'
  import { objectKeys } from '#app/lib/utils'
  import Spinner from '#components/spinner.svelte'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import type { ItemTransactionMode } from '#server/types/item'
  import { I18n, i18n } from '#user/lib/i18n'
  import ItemsByTransaction from './items_by_transaction.svelte'

  const {
    categoryItems = [],
    headers,
    category,
    displayCover,
    waitingForItems,
    showItemsOnMap,
  }: {
    categoryItems: SerializedItemWithUserData[]
    headers: { customIcon: string, label: string, backgroundColor: string }
    category: string
    displayCover: boolean
    waitingForItems: Promise<unknown>
    showItemsOnMap: (items: SerializedItemWithUserData[]) => void
  } = $props()

  const { customIcon, label, backgroundColor } = headers

  const itemsByTransactionsBase: Record<ItemTransactionMode, SerializedItemWithUserData[]> = {
    giving: [],
    lending: [],
    selling: [],
    inventorying: [],
  }

  const emptyList = $derived(categoryItems.length === 0)
  const categoryItemsByTransactions = $derived(groupBy(categoryItems, 'transaction'))
  const itemsByTransactions = $derived({ ...itemsByTransactionsBase, ...categoryItemsByTransactions })
  const categoryItemsWithAPosition = $derived(categoryItems.filter(({ position }) => position != null))
</script>

<div style:background-color={backgroundColor} class="items-list">
  <div class="header">
    <div class="category-title-wrapper">
      <h3
        class="category-title"
        class:emptyList
      >
        {@html icon(customIcon)}
        {I18n(label)}
      </h3>
    </div>
    {#if categoryItemsWithAPosition.length > 0}
      {#if category !== 'personal'}
        <button
          class="map-button"
          onclick={() => showItemsOnMap(categoryItemsWithAPosition)}
        >
          {@html icon('map-marker')}{I18n('show on map')}
        </button>
      {/if}
    {:else}
      <div class="emptyList">
        {i18n('nothing here')}
      </div>
    {/if}
  </div>
  {#await waitingForItems}
    <Spinner center={true} />
  {:then}
    {#if categoryItems.length > 0}
      <div class="items-list-per-transactions">
        {#each objectKeys(itemsByTransactions) as transaction (transaction)}
          <!-- Can't bind to itemsByTransactions as it's a derived state => binding_property_non_reactive -->
          <!-- eslint-disable-next-line svelte/no-unused-svelte-ignore -->
          <!-- svelte-ignore ownership_invalid_binding -->
          <ItemsByTransaction
            {transaction}
            transactionItems={itemsByTransactions[transaction]}
            {displayCover}
            {showItemsOnMap}
          />
        {/each}
      </div>
    {/if}
  {/await}
</div>
<style lang="scss">
  @import "general/scss/utils";
  .items-list-per-transactions{
    max-block-size: 15em;
    overflow-y: auto;
    margin-block-start: 0.3em;
  }
  .category-title{
    @include sans-serif;
    margin: 0;
    font-size: 1.1em;
    margin-inline-end: 1em;
  }
  .category-title-wrapper{
    @include display-flex(row, center, flex-start);
    :global(.fa){
      color: $dark-grey;
      inline-size: 2em;
    }
  }
  .header{
    @include display-flex(row, center, space-between);
  }
  .emptyList{
    padding: 0 0.5em;
    color: lighten($grey, 15%);
    font-size: 1em;
    :global(.fa){
      color: lighten($grey, 15%);
    }
  }
  .items-list{
    @include radius;
    padding: 0.5em;
    margin-block-end: 0.5em;
    /* background-color is defined by backgroundColor */
  }
  .map-button{
    @include tiny-button($light-grey, black);
  }
</style>
